<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  // Templates aprovados das caixas oficiais (WhatsApp Cloud) da conta —
  // só elas mandam template fora da janela de 24h pra reabrir a régua.
  officialTemplates: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const DEFAULTS = {
  max_touches: 5,
  min_gap_hours: 4,
  adaptive_timing: true,
  reopen_template_name: '',
};

const config = computed(() => ({ ...DEFAULTS, ...props.modelValue }));

const update = (key, value) => {
  emit('update:modelValue', { ...config.value, [key]: value });
};

// Só confirma no blur/enter (@change), não a cada tecla: emitir a cada
// @input mandava um PUT do funil inteiro por dígito, e um valor fora do
// intervalo (server valida 1-20 / 0-168) tocava o alerta de erro no meio da
// digitação. Valor fora do range simplesmente não emite — o campo mostra o
// que a pessoa digitou, mas a régua mantém o último valor válido salvo.
const updateIfValid = (key, rawValue, { integer = false, min, max } = {}) => {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return;
  if (integer && !Number.isInteger(value)) return;
  if (min !== undefined && value < min) return;
  if (max !== undefined && value > max) return;
  update(key, value);
};
</script>

<template>
  <div
    class="grid gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 sm:grid-cols-2"
  >
    <label class="flex flex-col gap-1 text-xs text-n-slate-11">
      {{ t('FUNNEL_TIMELINE.CADENCE_MAX_TOUCHES') }}
      <input
        data-test="max-touches"
        type="number"
        min="1"
        max="20"
        :value="config.max_touches"
        class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @change="
          updateIfValid('max_touches', $event.target.value, {
            integer: true,
            min: 1,
            max: 20,
          })
        "
      />
    </label>
    <label class="flex flex-col gap-1 text-xs text-n-slate-11">
      {{ t('FUNNEL_TIMELINE.CADENCE_MIN_GAP') }}
      <input
        data-test="min-gap"
        type="number"
        min="0"
        max="168"
        :value="config.min_gap_hours"
        class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @change="
          updateIfValid('min_gap_hours', $event.target.value, {
            min: 0,
            max: 168,
          })
        "
      />
    </label>
    <label
      class="flex items-center gap-2 text-xs text-n-slate-11 sm:col-span-2"
    >
      <input
        data-test="adaptive"
        type="checkbox"
        :checked="config.adaptive_timing"
        class="accent-n-brand"
        @change="update('adaptive_timing', $event.target.checked)"
      />
      {{ t('FUNNEL_TIMELINE.CADENCE_ADAPTIVE') }}
    </label>
    <label
      v-if="officialTemplates.length"
      class="flex flex-col gap-1 text-xs text-n-slate-11 sm:col-span-2"
    >
      {{ t('FUNNEL_TIMELINE.CADENCE_REOPEN_TEMPLATE') }}
      <select
        data-test="reopen-template"
        :value="config.reopen_template_name"
        class="reset-base rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12"
        @change="update('reopen_template_name', $event.target.value)"
      >
        <option value="">
          {{ t('FUNNEL_TIMELINE.CADENCE_REOPEN_NONE') }}
        </option>
        <option
          v-for="tpl in officialTemplates"
          :key="tpl.name"
          :value="tpl.name"
        >
          {{ tpl.name }}
        </option>
      </select>
    </label>
  </div>
</template>
